import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '../types';
import { authApi } from '../services/api';
import { socketService } from '../services/socket';
import toast from 'react-hot-toast';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true });
          const response = await authApi.login(credentials);

          // Store token
          localStorage.setItem('access_token', response.access_token);

          // Update state
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false,
          });

          // Connect socket
          socketService.connect(response.access_token);

          toast.success('Login realizado com sucesso!');
        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear state
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');

          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });

          // Disconnect socket
          socketService.disconnect();

          toast.success('Logout realizado com sucesso!');
        }
      },

      checkAuth: async () => {
        const token = localStorage.getItem('access_token');

        if (!token) {
          set({ isAuthenticated: false, user: null, token: null });
          return;
        }

        try {
          set({ isLoading: true });
          const user = await authApi.getCurrentUser();

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });

          // Connect socket
          socketService.connect(token);
        } catch (error) {
          // Invalid token
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user: User) => {
        set({ user });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
